import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Package, ShieldCheck, ShieldAlert, Home, Info, Calendar, Wrench, MapPin } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(String(s).slice(0, 10));
    if (isNaN(d.getTime())) return String(s);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return String(s); }
}

function statusBadge(fromStr, toStr) {
  if (!toStr) return { label: "—", cls: "bg-slate-500/10 text-slate-600 border-slate-200" };
  const to = new Date(String(toStr).slice(0, 10)).getTime();
  const now = Date.now();
  if (to < now) return { label: "Expired", cls: "bg-red-500/10 text-red-700 border-red-200" };
  const days = Math.round((to - now) / 86400000);
  if (days <= 30) return { label: "Ending soon", cls: "bg-amber-500/10 text-amber-700 border-amber-200" };
  return { label: "Valid", cls: "bg-emerald-500/10 text-emerald-700 border-emerald-200" };
}

export default function PublicAssetPage() {
  const { asset_id } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/api/public/hardware/${asset_id}`)
      .then(r => setItem(r.data))
      .catch(() => setError("Asset not found or has been removed."))
      .finally(() => setLoading(false));
  }, [asset_id]);

  const warr = statusBadge(item?.initial_warranty_from, item?.initial_warranty_to);
  const amc = statusBadge(item?.amc_from, item?.amc_to);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white" data-testid="public-asset-page">
      {/* Top brand strip */}
      <div className="h-1.5 w-full flex" aria-hidden>
        <div className="flex-1 bg-brand-blue" />
        <div className="w-16 bg-brand-orange" />
        <div className="w-16 bg-emerald-500" />
      </div>

      <div className="max-w-3xl mx-auto p-4 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-2 text-brand-blue font-semibold">
            <img src="/logo.png" alt="KELTA" className="w-8 h-8 rounded" onError={e => { e.target.style.display = "none"; }} />
            <span className="text-lg">KELTA</span>
          </Link>
          <Link to="/">
            <Button variant="outline" size="sm">
              <Home className="w-4 h-4 mr-1.5" /> Portal home
            </Button>
          </Link>
        </div>

        {loading ? (
          <Card className="p-10 text-center text-muted-foreground">Loading asset…</Card>
        ) : error ? (
          <Card className="p-10 text-center" data-testid="public-asset-not-found">
            <Info className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <h2 className="font-semibold text-lg">{error}</h2>
            <p className="text-sm text-muted-foreground mt-1">If you scanned a QR label, please contact the asset owner.</p>
          </Card>
        ) : (
          <>
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
                  <Package className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] tracking-[0.14em] font-semibold text-muted-foreground uppercase">Asset</p>
                  <p className="text-brand-blue font-mono text-sm font-bold mt-0.5" data-testid="public-asset-id">{item.asset_id}</p>
                  <h1 className="text-2xl font-bold mt-1">{item.item_name || "Untitled Item"}</h1>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {item.brand && <Badge className="bg-brand-blue/10 text-brand-blue border-0 text-[11px]">{item.brand}</Badge>}
                    {item.subtype && <Badge className="bg-brand-orange/10 text-brand-orange border-0 text-[11px]">{item.subtype}</Badge>}
                    {item.present_condition && <Badge className="bg-emerald-500/10 text-emerald-700 border-0 text-[11px]">{item.present_condition}</Badge>}
                  </div>
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                <Tile icon={MapPin} label="Location / Institution" value={item.institution_name} />
                <Tile icon={Calendar} label="Installation Date" value={fmtDate(item.date_of_supply)} />
                <Tile icon={Package} label="Make / Model" value={[item.brand, item.manufacturer_model].filter(Boolean).join(" ") || "—"} />
                <Tile icon={Wrench} label="Asset Type" value={item.subtype || item.category || "—"} />
                <Tile icon={Info} label="Serial Number" value={item.serial_no || "—"} />
                <Tile icon={item.status === "Active" || !item.status ? ShieldCheck : ShieldAlert} label="Status" value={item.status || "Active"} />
              </div>
            </Card>

            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs tracking-widest font-semibold text-muted-foreground uppercase">Warranty</div>
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-0.5 border ${warr.cls}`}>
                    {warr.label === "Valid" ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                    {warr.label}
                  </span>
                </div>
                <div className="mt-3 text-sm">
                  <div><span className="text-muted-foreground">From: </span>{fmtDate(item.initial_warranty_from)}</div>
                  <div><span className="text-muted-foreground">Until: </span>{fmtDate(item.initial_warranty_to)}</div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs tracking-widest font-semibold text-muted-foreground uppercase">AMC</div>
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-0.5 border ${amc.cls}`}>
                    {amc.label}
                  </span>
                </div>
                <div className="mt-3 text-sm">
                  <div><span className="text-muted-foreground">From: </span>{fmtDate(item.amc_from)}</div>
                  <div><span className="text-muted-foreground">Until: </span>{fmtDate(item.amc_to)}</div>
                </div>
              </Card>
            </div>

            {/* Complaint & Service history */}
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <Card className="p-4" data-testid="public-complaint-history">
                <div className="text-xs tracking-widest font-semibold text-muted-foreground uppercase mb-3">Complaint History</div>
                {(item.complaint_history || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No complaints recorded.</p>
                ) : (
                  <ul className="space-y-2">
                    {item.complaint_history.map((c, i) => (
                      <li key={i} className="text-sm border-b border-border/60 last:border-0 pb-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs font-bold">{c.comp_id || `C-${i + 1}`}</span>
                          <span className={`text-[11px] font-semibold ${c.solved === "Yes" ? "text-emerald-700" : "text-amber-700"}`}>
                            {c.solved === "Yes" ? "Solved" : c.solved === "No" ? "Open" : ""}
                          </span>
                        </div>
                        <div className="text-muted-foreground">{c.complaint_remarks || "—"}</div>
                        <div className="text-xs text-muted-foreground">{fmtDate(c.reporting_date)}{c.service_date ? ` → serviced ${fmtDate(c.service_date)}` : ""}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
              <Card className="p-4" data-testid="public-service-history">
                <div className="text-xs tracking-widest font-semibold text-muted-foreground uppercase mb-3">Service History</div>
                {(item.service_history || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No services recorded.</p>
                ) : (
                  <ul className="space-y-2">
                    {item.service_history.map((s, i) => (
                      <li key={i} className="text-sm border-b border-border/60 last:border-0 pb-2">
                        <div className="font-medium">{s.service_details || "Service"}</div>
                        <div className="text-xs text-muted-foreground">
                          {fmtDate(s.service_date)}{s.next_service_date ? ` · next ${fmtDate(s.next_service_date)}` : ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            <Card className="p-4 mt-4 bg-muted/30">
              <p className="text-xs text-muted-foreground">
                This is a public asset summary. Sensitive data (bills, vendor contacts, purchase amounts) is available only to the registered owner after logging in.
              </p>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-white">
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.12em] font-semibold text-muted-foreground uppercase">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-sm font-semibold mt-1 break-words">{value || "—"}</div>
    </div>
  );
}
